import React from "react";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Eye, Edit, Trash2, Folder, FolderX } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Contract, ContractGroup } from "@/lib/types";
import { useNavigate } from "react-router-dom";
import { importanceBadges, BadgeVariant } from "./types";
import { ExpirySticker } from "./ExpirySticker";
import { GroupBadge } from "../group/GroupBadge";
import { useContractGroups } from "@/hooks/useContractGroups";
import { useImportanceColors } from "@/hooks/useImportanceColors";

interface MobileContractCardProps {
  contract: Contract;
  isColumnSelected: (id: string) => boolean;
  selectedContracts: Set<string>;
  toggleContractSelection: (id: string) => void;
  handleDeleteContract: (id: string) => Promise<void>;
  handleContractTap: (contract: Contract, event: React.MouseEvent) => void;
  handleContractTouchStart: (contract: Contract, event: React.TouchEvent | React.MouseEvent) => void;
  handleContractTouchEnd: () => void;
  handleContractTouchMove: () => void;
  onGroupContract?: (contractId: string, groupId: string) => Promise<void>;
  onRemoveFromGroup?: (contractId: string) => Promise<void>;
}

export function MobileContractCard({
  contract,
  isColumnSelected,
  selectedContracts,
  toggleContractSelection,
  handleDeleteContract,
  handleContractTap,
  handleContractTouchStart,
  handleContractTouchEnd,
  handleContractTouchMove,
  onGroupContract,
  onRemoveFromGroup
}: MobileContractCardProps) {
  const navigate = useNavigate();
  const { useContractGroupsQuery } = useContractGroups();
  const { data: contractGroups = [] } = useContractGroupsQuery();
  const { getImportanceColor } = useImportanceColors();

  const handleAssignToGroup = async (groupId: string) => {
    if (onGroupContract) {
      try {
        await onGroupContract(contract.id, groupId);
      } catch (error) {
        console.error("Error assigning to group:", error);
      }
    }
  };

  const handleRemoveFromGroup = async () => {
    if (onRemoveFromGroup) {
      try {
        await onRemoveFromGroup(contract.id);
      } catch (error) {
        console.error("Error removing from group:", error);
      }
    }
  };

  return (
    <Card 
      className="mb-4 border-gray-800 bg-gray-950 relative hover:bg-gray-900 transition-colors"
      onClick={(e) => handleContractTap(contract, e)}
      onTouchStart={(e) => handleContractTouchStart(contract, e)}
      onTouchEnd={handleContractTouchEnd}
      onTouchMove={handleContractTouchMove}
      onMouseDown={(e) => handleContractTouchStart(contract, e)}
      onMouseUp={handleContractTouchEnd}
      onMouseLeave={handleContractTouchEnd}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={selectedContracts.has(contract.id)}
            onCheckedChange={() => toggleContractSelection(contract.id)}
            aria-label={`Select ${contract.supplierName}`}
            className="mt-1 border-blue-500 text-blue-500 contract-checkbox"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex-1 space-y-2">
            <div className="flex items-start justify-between">
              <h3 className="font-medium text-white">{contract.supplierName}</h3>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 contract-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/contracts/${contract.id}`);
                    }}
                    className="flex items-center"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    <span>View</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/contracts/${contract.id}/edit`);
                    }}
                    className="flex items-center"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    <span>Edit</span>
                  </DropdownMenuItem>
                  
                  {onGroupContract && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => e.stopPropagation()}
                        disabled={!contractGroups.length}
                      >
                        <Folder className="h-4 w-4 mr-2" />
                        <span>Assign to Group</span>
                      </DropdownMenuItem>
                      {contractGroups.length > 0 && (
                        <DropdownMenu>
                          {contractGroups.map((group: ContractGroup) => (
                            <DropdownMenuItem 
                              key={group.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssignToGroup(group.id);
                              }}
                              className="pl-8"
                            >
                              {group.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenu>
                      )}
                      
                      {contract.group && onRemoveFromGroup && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromGroup();
                          }}
                        >
                          <FolderX className="h-4 w-4 mr-2" />
                          <span>Remove from Group</span>
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                  
                  <DropdownMenuSeparator />
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <DropdownMenuItem 
                        onSelect={(e) => e.preventDefault()}
                        className="text-destructive focus:bg-destructive/10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        <span>Delete</span>
                      </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete contract?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete the contract
                          and all associated data.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleDeleteContract(contract.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {isColumnSelected("importance") && (
                (() => {
                  const { className, style } = getImportanceColor(contract.importance);
                  return (
                    <Badge 
                      variant={importanceBadges[contract.importance].variant as BadgeVariant}
                      className={className}
                      style={style}
                    >
                      {importanceBadges[contract.importance].label}
                    </Badge>
                  );
                })()
              )}
              
              {contract.group && (
                <GroupBadge 
                  groupName={contract.group.name}
                  groupDescription={contract.group.description}
                />
              )}
              
              {isColumnSelected("expiryStatus") && (
                <ExpirySticker contract={contract} />
              )}
              
              {isColumnSelected("type") && (
                <span className="text-gray-400">{contract.type.name}</span>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              {isColumnSelected("category") && (
                <>
                  <div className="text-gray-400">Category:</div>
                  <div className="text-gray-300">{contract.category?.name || "—"}</div>
                </>
              )}
              
              {isColumnSelected("expiryDate") && (
                <>
                  <div className="text-gray-400">Expiry Date:</div>
                  <div className="text-gray-300">{format(contract.expiryDate, "MMM d, yyyy")}</div>
                </>
              )}
              
              {isColumnSelected("signedDate") && (
                <>
                  <div className="text-gray-400">Signed Date:</div>
                  <div className="text-gray-300">{format(contract.signedDate, "MMM d, yyyy")}</div>
                </>
              )}
              
              {isColumnSelected("expectedCost") && (
                <>
                  <div className="text-gray-400">Cost:</div>
                  <div className="text-gray-300">
                    {contract.currency ? `${contract.currency.symbol}${contract.expectedCost}` : contract.expectedCost}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
